import { Component, OnInit, OnDestroy, inject } from "@angular/core";
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef
} from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { Observable, of, Subject, timer } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";

import { MilestonesService } from "@shared/services/milestones/milestones.service";
import { PayCycleService } from "@shared/services/pay-cycle/pay-cycle.service";
import { ToastService } from "@shared/services/toast/toast.service";
import { Milestone, OffCycleCreateDTO, PayCycle } from "src/app/shared/models/pay-cycle.interface";
import { UpdateSelectedCalendarMonth } from "../../../../store/actions/payCycleSelect.action";
import { AppState } from "../../../../store/models/state.model";
import { formatDateService } from "../../_services/formatDate.service";

@Component({
	selector: "app-create-offcycle-dialog",
	templateUrl: "./create-offcycle-dialog.component.html",
	styleUrls: ["./create-offcycle-dialog.component.scss"]
})
export class CreateOffcycleDialogComponent implements OnInit, OnDestroy {
	createOffCycleForm!: FormGroup;
	destroy$ = new Subject<void>();
	startDate!: Date;
	endDate!: Date;

	isEditing = false;
	milestones: Milestone[] = [];

	private readonly PAYROLL_CYCLE_ID_VALIDATION_DEBOUNCE_MS = 500;

	private formBuilder = inject(FormBuilder);
	private payCycleService = inject(PayCycleService);
	private milestonesService = inject(MilestonesService);
	private toastService = inject(ToastService);
	public dialogRef = inject(MatDialogRef<CreateOffcycleDialogComponent>);
	private formatDateService = inject(formatDateService);
	private store = inject(Store<AppState>);
	public data = inject(MAT_DIALOG_DATA);

	ngOnInit() {
		if (this.data.dialogType === "CREATE") {
			this.isEditing = false;
		} else {
			this.isEditing = true;
		}

		this.initializeForm();
	}

	initializeForm() {
		this.createOffCycleForm = this.formBuilder.group(
			{
				payrollCycleId: new FormControl("", {
					updateOn: "change",
					asyncValidators: [this.checkPayrollCycleIdUnique.bind(this)]
				}),
				payrollCycleName: [""],
				cycleId: ["", Validators.required],
				cycleName: ["", Validators.required],
				cycleStartDate: ["", Validators.required],
				cycleEndDate: ["", Validators.required],
				offCycleReason: [""]
			},
			{ validator: [this.checkDates] }
		);

		if (this.isEditing) {
			this.createOffCycleForm.get("payrollCycleId")!.patchValue(this.data.payCycle.payrollCycleId);
			this.createOffCycleForm.get("payrollCycleName")!.patchValue(this.data.payCycle.payrollCycleName);
			this.createOffCycleForm.get("cycleId")!.patchValue(this.data.externalId);
			this.createOffCycleForm.get("cycleName")!.patchValue(this.data.payCycle.name);
			this.createOffCycleForm.get("cycleStartDate")!.patchValue(this.data.payCycle.start);
			this.createOffCycleForm.get("cycleEndDate")!.patchValue(this.data.payCycle.end);
			this.createOffCycleForm.get("offCycleReason")!.patchValue(this.data.payCycle.comment);

			if (this.data.payCycle.attributes?.G2N_APPROVED && this.data.payCycle.attributes?.G2N_APPROVED === true) {
				this.createOffCycleForm.get("payrollCycleId")?.disable();
			}
		}
	}

	checkPayrollCycleIdUnique: AsyncValidatorFn = (control: AbstractControl) => {
		if (control.disabled) return of(null);

		// Delay the validation; Angular cancels the previous observable when value changes
		return timer(this.PAYROLL_CYCLE_ID_VALIDATION_DEBOUNCE_MS).pipe(
			switchMap(() => {
				const payrollCycleId = (control.value ?? "").toString().trim();
				if (!payrollCycleId) return of(null);

				if (this.isEditing && this.data?.payCycle?.payrollCycleId === payrollCycleId) {
					return of(null);
				}

				if (!/^\d+$/.test(payrollCycleId)) {
					return of({ payrollCycleIdNotValid: true });
				}

				return this.payCycleService.validatePayrollCycleId(payrollCycleId).pipe(
					map(res => (res?.valid ? null : { payrollCycleIdNotUnique: res?.message ?? true })),
					catchError(error => {
						console.error(error);
						return of({ payrollCycleIdValidationError: true });
					})
				);
			}),
			take(1)
		);
	};

	checkDates(group: FormGroup) {
		// end date is before start date
		if (new Date(group.controls.cycleEndDate.value) < new Date(group.controls.cycleStartDate.value)) {
			return { endDateNotValid: true };
		}

		// start date is in the past or after end date
		if (new Date(group.controls.cycleEndDate.value) < new Date(group.controls.cycleStartDate.value)) {
			return { startDateNotValid: true };
		}

		return null;
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	onCancel(): void {
		this.dialogRef.close(false);
	}

	onConfirm(): void {
		const formData = this.createOffCycleForm.getRawValue();

		const offCycleObject: OffCycleCreateDTO = {
			customerId: this.data.customerId,
			legalEntityId: this.data.legalEntityId,
			payGroupId: this.data.payGroupId,
			externalId: formData.cycleId,
			name: formData.cycleName,
			comment: formData.offCycleReason,
			payrollCycleId: formData.payrollCycleId,
			payrollCycleName: formData.payrollCycleName
		};

		if (this.isEditing) {
			offCycleObject.id = this.data.payCycle.id;
			offCycleObject.version = this.data.payCycle.version;
			//offCycleObject.milestones = this.milestones;

			if (!this.createOffCycleForm.get("cycleStartDate")?.dirty) {
				const date = new Date(formData.cycleStartDate);
				offCycleObject.start = date.toISOString().split("T")[0];
			} else {
				offCycleObject.start = this.formatDateService.formatDate(formData.cycleStartDate);
			}

			if (!this.createOffCycleForm.get("cycleEndDate")?.dirty) {
				const date = new Date(formData.cycleEndDate);
				offCycleObject.end = date.toISOString().split("T")[0];
			} else {
				offCycleObject.end = this.formatDateService.formatDate(formData.cycleEndDate);
			}

			this.editOffCycle(offCycleObject)
				.pipe(take(1))
				.subscribe({
					next: data => {
						this.toastService.showSuccess("Off Cycle Edited");
						this.dialogRef.close(data.id);
						this.setNewCalendarState(data);
					},
					error: () => {
						this.toastService.showError("Error editing off cycle, please try again");
					}
				});
		} else {
			const startDate = this.formatDateService.formatDate(formData.cycleStartDate);
			const endDate = this.formatDateService.formatDate(formData.cycleEndDate);
			offCycleObject.start = startDate;
			offCycleObject.end = endDate;
			this.createOffCycle(offCycleObject)
				.pipe(take(1))
				.subscribe({
					next: data => {
						this.toastService.showSuccess("Off Cycle created");
						this.dialogRef.close(data.id);
						this.setNewCalendarState(data);
					},
					error: () => {
						this.toastService.showError("Error creating off cycle, please try again");
					}
				});
		}
	}

	editOffCycle(offCycleObject: OffCycleCreateDTO): Observable<PayCycle> {
		return this.payCycleService.editOffCycle(offCycleObject);
	}

	createOffCycle(offCycleObject: OffCycleCreateDTO): Observable<PayCycle> {
		return this.payCycleService.createOffCycle(offCycleObject);
	}

	getMilestones(payCycleId: string): void {
		this.milestonesService
			.getMilestones(payCycleId)
			.pipe(take(1))
			.subscribe(x => {
				this.milestones = x;
			});
	}

	setNewCalendarState(data: PayCycle) {
		this.store.dispatch(
			new UpdateSelectedCalendarMonth({
				selectedMonth: {
					month: new Date(data.start).getMonth(),
					paygroupId: data.payGroupId,
					payCycle: data
				}
			})
		);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
